import { exportMapImage } from "./export-image.js";
import { loadGpxFromFile, loadGpxFromUrl } from "./gpx.js";
import { createMap, fitBoundsAndWait } from "./map.js";

export function bootInstance(root) {
  const mapEl = root.querySelector(".lgpxp__map");
  const controls = root.querySelector(".lgpxp__controls");
  const gpxInput = root.querySelector(".lgpxp__gpxInput");
  const pdfBtn = root.querySelector(".lgpxp__pdfBtn");
  const orientationSelect = root.querySelector(".lgpxp__orientation");
  const height = root.dataset.height || "600px";

  pdfBtn.disabled = true;
  pdfBtn.setAttribute("aria-disabled", "true");
  mapEl.style.height = height;

  const map = createMap(mapEl, {
    thunderforestUrl: root.dataset.thunderforestUrl,
  });
  let currentGpxLayer = null;
  let lastGpxBounds = null;

  gpxInput.addEventListener("change", (e) => {
    const file = e.target.files?.[0];
    if (file) loadGPXFromFile(file);
    e.target.value = "";
  });

  const dropzone = controls.querySelector(".lgpxp__dropzone");
  ["dragenter", "dragover"].forEach((eventName) =>
    dropzone.addEventListener(eventName, (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.add("dragover");
    })
  );
  ["dragleave", "drop"].forEach((eventName) =>
    dropzone.addEventListener(eventName, (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.remove("dragover");
    })
  );
  dropzone.addEventListener("drop", (e) => {
    const file = e.dataTransfer?.files?.[0];
    if (file) loadGPXFromFile(file);
  });

  pdfBtn.addEventListener("click", () => {
    exportMapImage({
      root,
      map,
      mapEl,
      currentGpxLayer,
      lastGpxBounds,
      orientation: getOrientation(),
      onRestore: refitAfterRestore,
    });
  });

  const initialGpxUrl = getInitialGpxUrl();
  if (initialGpxUrl) {
    loadGPXFromUrl(initialGpxUrl);
  }

  async function loadGPXFromFile(file) {
    if (!file) return;

    await loadGPX(() => loadGpxFromFile(file, map), "Failed to load GPX: ");
  }

  async function loadGPXFromUrl(url) {
    await loadGPX(
      () => loadGpxFromUrl(url, map),
      "Failed to load GPX from URL: "
    );
  }

  async function loadGPX(load, errorPrefix) {
    clearCurrentGpx();
    setExportEnabled(false);

    try {
      const result = await load();
      if (!result) return;

      currentGpxLayer = result.layer;
      if (result.bounds.isValid()) {
        await fitBoundsAndWait(map, result.bounds, { padding: [20, 20] });
        lastGpxBounds = result.bounds;
        setExportEnabled(true);
      }
    } catch (e) {
      alert(errorPrefix + (e?.message || e));
      console.error(e);
    }
  }

  function clearCurrentGpx() {
    if (currentGpxLayer) {
      map.removeLayer(currentGpxLayer);
      currentGpxLayer = null;
      lastGpxBounds = null;
    }
  }

  function getOrientation() {
    return orientationSelect?.value === "landscape" ? "landscape" : "portrait";
  }

  function setExportEnabled(enabled) {
    pdfBtn.disabled = !enabled;
    if (enabled) {
      pdfBtn.removeAttribute("aria-disabled");
      return;
    }

    pdfBtn.setAttribute("aria-disabled", "true");
  }

  function getInitialGpxUrl() {
    return new URLSearchParams(window.location.search).get("gpx");
  }

  function refitAfterRestore() {
    const bounds =
      lastGpxBounds || (currentGpxLayer && currentGpxLayer.getBounds());
    if (!bounds || !bounds.isValid()) return;

    setTimeout(() => {
      map.invalidateSize(true);
      fitBoundsAndWait(map, bounds, { padding: [20, 20] });
    }, 50);
  }
}
